/* global axios */
import { ref, onBeforeUnmount } from 'vue';
import { useStore } from 'dashboard/composables/store';
import types from 'dashboard/store/mutation-types';
import { whatsappStage } from './visorSteps';

// Mesmo caminho do assistente de caixa (settings/inbox/channels/WhatsappWeb.vue):
// caixa Channel::Api + sessão WAHA + QR renovado a cada 15 s + status a cada 3 s.
const QR_REFRESH_MS = 15000;
const STATUS_POLL_MS = 3000;

const SESSION_DEFAULTS = {
  engine: 'GOWS',
  ignore_status: true,
  ignore_groups: false,
  ignore_channels: true,
  ignore_broadcast: true,
  lock_single_conversation: true,
  show_agent_name: true,
  enable_commands: true,
  mark_as_read: true,
  locale: 'pt-BR',
};

export const useWahaQr = ({ accountId, onInboxCreated, onConnected }) => {
  const store = useStore();
  const inboxId = ref(null);
  const qrCode = ref(null);
  const stage = ref('preparando');
  const error = ref(false);
  const secondsLeft = ref(QR_REFRESH_MS / 1000);
  let qrFetchedAt = 0;
  let statusTimer = null;
  let clockTimer = null;

  const base = () =>
    `/api/v1/accounts/${accountId.value}/inboxes/${inboxId.value}/waha`;

  const stop = () => {
    clearInterval(statusTimer);
    clearInterval(clockTimer);
    statusTimer = null;
    clockTimer = null;
  };

  const fetchQr = async () => {
    try {
      const { data } = await axios.get(`${base()}/qr_code`);
      qrCode.value = data.qr_code || null;
      qrFetchedAt = Date.now();
      if (data.status) stage.value = whatsappStage(data.status, stage.value);
    } catch {
      // A próxima volta do status tenta de novo.
    }
  };

  const tick = () => {
    const left = QR_REFRESH_MS - (Date.now() - qrFetchedAt);
    secondsLeft.value = Math.max(1, Math.ceil(left / 1000));
  };

  const pollStatus = async () => {
    try {
      const { data } = await axios.get(`${base()}/status`);
      stage.value = whatsappStage(data.status, stage.value);
      error.value = stage.value === 'erro';
      if (stage.value === 'conectado') {
        stop();
        onConnected?.(inboxId.value);
      } else if (
        data.status === 'SCAN_QR_CODE' &&
        Date.now() - qrFetchedAt >= QR_REFRESH_MS
      ) {
        fetchQr();
      }
    } catch {
      // Silencioso: segue tentando.
    }
  };

  const startPolling = () => {
    stop();
    statusTimer = setInterval(pollStatus, STATUS_POLL_MS);
    clockTimer = setInterval(tick, 1000);
    fetchQr();
  };

  const createSession = async () => {
    const { data } = await axios.post(
      `${base()}/create_session`,
      SESSION_DEFAULTS
    );
    if (!data?.session_name) return;
    store.commit(`inboxes/${types.UPDATE_WAHA_STATUS}`, {
      inboxId: inboxId.value,
      wahaSessionName: data.session_name,
      wahaStatus: data.status,
      wahaStatusCheckedAt: data.checked_at,
    });
  };

  // Retoma a caixa que o onboarding já criou (a sessão pode seguir viva) ou
  // cria uma nova.
  const start = async ({ existingInboxId, inboxName }) => {
    error.value = false;
    stage.value = 'preparando';
    try {
      if (existingInboxId) {
        inboxId.value = existingInboxId;
        const { data } = await axios.get(`${base()}/status`);
        if (['NOT_FOUND', 'NOT_CONFIGURED'].includes(data.status)) {
          await createSession();
        }
      } else {
        const inbox = await store.dispatch('inboxes/createChannel', {
          name: inboxName,
          channel: { type: 'api', additional_attributes: {} },
        });
        inboxId.value = inbox.id;
        await onInboxCreated?.(inbox.id);
        await createSession();
      }
      startPolling();
    } catch {
      error.value = true;
    }
  };

  onBeforeUnmount(stop);

  return { inboxId, qrCode, stage, error, secondsLeft, start, stop };
};
